/**
 * @fileoverview Modal component for viewing and editing patient details
 * Uses DmsModal with tabs for Details and History
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { intl, fetchData, HTTP_STATUS, DmsModal, DmsTabber, DmsButton, DmsTable } from "dmsbasereact";
import { Box, CircularProgress, useMediaQuery, useTheme, Chip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import HistoryIcon from "@mui/icons-material/History";
import DescriptionIcon from "@mui/icons-material/Description";
import { PatientPrescriptionHistoryTab } from "../PatientPrescriptionHistoryTab";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { ViewModeContent } from "./ViewModeContent";
import { EditModeContent } from "./EditModeContent";
import { PatientStatusEnum } from "../../constants/PatientStatusEnum";
import { PatientStatusReason } from "../../constants/PatientStatusReason";

/**
 * Prepare form data from patient object for editing
 */
const prepareFormData = (patient) => {
  return {
    name: patient.name || "",
    cpf: patient.cpf || "",
    rg: patient.rg || "",
    rg_issuing_agency: patient.rg_issuing_agency || "",
    birth_date: patient.birth_date ? patient.birth_date.split("T")[0] : "",
    gender: patient.gender || "",
    email: patient.email || "",
    phone: patient.phone || "",
    home_phone: patient.home_phone || "",
    has_pregnancy_potential: patient.has_pregnancy_potential || false,
    menstrual_cycle_regular: patient.menstrual_cycle_regular || false,
    status: patient.status ?? PatientStatusEnum.ACTIVE,
    patient_status_reason_pk: patient.patient_status_reason_pk || null,
    status_reason: patient.status_reason || "",
    contact_authorization: patient.contact_authorization,
    // Address fields
    patient_address_pk: patient.address?.pk || null,
    zip_code: patient.address?.zip_code || "",
    address: patient.address?.address || "",
    address_number: patient.address?.number || "",
    city: patient.address?.city || "",
    address_state: patient.address?.state || "",
    // Fertility fields (loaded separately)
    highlyEffectiveMethods: patient._highlyEffectiveMethods || [],
    barrierMethods: patient._barrierMethods || [],
    selectedInfertilityReason: patient._infertilityReasonPk || null,
    // Responsible Legal fields
    hasResponsibleLegal: !!patient.responsible_legal,
    responsibleLegal_pk: patient.responsible_legal?.pk || null,
    responsibleLegal_name: patient.responsible_legal?.name || "",
    responsibleLegal_cpf: patient.responsible_legal?.cpf || "",
    responsibleLegal_birth_date: patient.responsible_legal?.birth_date ? patient.responsible_legal.birth_date.split("T")[0] : "",
    responsibleLegal_rg: patient.responsible_legal?.rg || "",
    responsibleLegal_rg_issuing_agency: patient.responsible_legal?.rg_issuing_agency || "",
    responsibleLegal_email: patient.responsible_legal?.email || "",
    responsibleLegal_phone: patient.responsible_legal?.phone || "",
    sameAddressAsPatient: !!(patient.responsible_legal?.address?.pk && patient.address?.pk && patient.responsible_legal.address.pk === patient.address.pk),
    responsibleLegal_zip_code: patient.responsible_legal?.address?.zip_code || "",
    responsibleLegal_address: patient.responsible_legal?.address?.address || "",
    responsibleLegal_number: patient.responsible_legal?.address?.number || "",
    responsibleLegal_city: patient.responsible_legal?.address?.city || "",
    responsibleLegal_state: patient.responsible_legal?.address?.state || "",
  };
};

/**
 * Build update payload from form data
 */
const buildUpdatePayload = (formData, originalData) => {
  const payload = {
    name: formData.name,
    cpf: formData.cpf,
    rg: formData.rg || null,
    rg_issuing_agency: formData.rg_issuing_agency || null,
    birth_date: formData.birth_date,
    gender: formData.gender,
    email: formData.email || null,
    phone: formData.phone?.replace(/\D/g, "") || null,
    home_phone: formData.home_phone?.replace(/\D/g, "") || null,
    has_pregnancy_potential: formData.has_pregnancy_potential,
    menstrual_cycle_regular: formData.menstrual_cycle_regular,
    status: formData.status,
    contact_authorization: formData.contact_authorization,
  };

  // If status is changing, include reason fields
  if (originalData && formData.status !== originalData.status) {
    payload.patient_status_reason_pk = formData.patient_status_reason_pk ? Number(formData.patient_status_reason_pk) : null;
    payload.status_reason = formData.status_reason || null;
  }

  // Include address (all fields are required - validated by HTML5 required attribute)
  payload.address = {
    address_name: formData.name, // Use patient name as address name
    zip_code: formData.zip_code || "",
    address: formData.address || "",
    number: formData.address_number || "",
    city: formData.city || "",
    state: formData.address_state || "",
    country_code: "BR",
  };

  if (formData.hasResponsibleLegal && formData.responsibleLegal_name) {
    payload.responsible_legal = {
      ...(formData.responsibleLegal_pk ? { pk: formData.responsibleLegal_pk } : {}),
      name: formData.responsibleLegal_name,
      cpf: formData.responsibleLegal_cpf?.replace(/\D/g, "") || "",
      birth_date: formData.responsibleLegal_birth_date || null,
      rg: formData.responsibleLegal_rg || null,
      rg_issuing_agency: formData.responsibleLegal_rg_issuing_agency || null,
      email: formData.responsibleLegal_email || null,
      phone: formData.responsibleLegal_phone?.replace(/\D/g, "") || null,
      ...(formData.sameAddressAsPatient
        ? { address_pk: formData.patient_address_pk }
        : {
            address: {
              address_name: formData.responsibleLegal_name,
              zip_code: formData.responsibleLegal_zip_code || "",
              address: formData.responsibleLegal_address || "",
              number: formData.responsibleLegal_number || "",
              city: formData.responsibleLegal_city || "",
              state: formData.responsibleLegal_state || "",
              country_code: "BR",
            },
          }),
    };
  }

  return payload;
};

/**
 * History tab header configuration
 */
const getHistoryHeader = () => [
  {
    label: intl.get("change_type").d("Change Type"),
    column_name: "change_type",
    sortable: false,
    element: (row) => {
      switch (row.change_type) {
        case "ACTIVATED":
          return <Chip label={intl.get("Activated").d("Activated")} color="success" size="small" />;
        case "DEACTIVATED":
          return <Chip label={intl.get("Deactivated").d("Deactivated")} color="error" size="small" />;
        case "CREATED":
          return <Chip label={intl.get("Created").d("Created")} color="primary" size="small" />;
        default:
          return <Chip label={row.change_type} size="small" />;
      }
    }
  },
  {
    label: intl.get("Reason").d("Reason"),
    column_name: "reason",
    sortable: false,
    function: (reason) => reason ? PatientStatusReason.getText(reason) : '-'
  },
  {
    label: intl.get("change_date").d("Date"),
    column_name: "updated_on",
    sortable: true,
    function: (date) => date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-'
  }
];

/**
 * Modal for viewing and editing patient details with history tab
 */
export const PatientDetailModal = ({
  open,
  onClose,
  patient = null,
  loading = false,
  onSave = null,
  onPatientUpdated = null,
  canEdit = true,
  startInEditMode = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const historyTable = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Fertility data state
  const [enrichedPatient, setEnrichedPatient] = useState(null);
  const [fertilityLoading, setFertilityLoading] = useState(false);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasAutoEnteredEditMode, setHasAutoEnteredEditMode] = useState(false);

  // Load fertility data for a patient
  const loadFertilityData = useCallback(async (patientData) => {
    if (!patientData || patientData.gender !== "F") {
      return patientData;
    }

    let highlyEffectiveMethods = [];
    let barrierMethods = [];
    let infertilityReasonPk = null;

    // Use data that's already loaded in patientData (from /patient/{pk}/details)
    // This avoids making additional requests that may return cached data
    if (patientData.has_pregnancy_potential) {
      // Extract PKs from the already-loaded highly_effective_methods and barrier_methods
      highlyEffectiveMethods = (patientData.highly_effective_methods || []).map(m => m.pk);
      barrierMethods = (patientData.barrier_methods || []).map(m => m.pk);
    } else {
      // Use the infertility_reason.pk that's already loaded
      if (patientData.infertility_reason) {
        infertilityReasonPk = patientData.infertility_reason.pk;
      }
    }

    return {
      ...patientData,
      _highlyEffectiveMethods: highlyEffectiveMethods,
      _barrierMethods: barrierMethods,
      _infertilityReasonPk: infertilityReasonPk,
    };
  }, []);

  // Load fertility data when patient changes
  useEffect(() => {
    const loadData = async () => {
      setFertilityLoading(true);
      const enriched = await loadFertilityData(patient);
      setEnrichedPatient(enriched);
      setFertilityLoading(false);
    };

    if (patient) {
      loadData();
    } else {
      setEnrichedPatient(null);
    }
  }, [patient, loadFertilityData]);

  // Auto-enter edit mode if startInEditMode is true
  useEffect(() => {
    if (startInEditMode && enrichedPatient && !hasAutoEnteredEditMode && !fertilityLoading) {
      const prepared = prepareFormData(enrichedPatient);
      setFormData(prepared);
      setIsEditMode(true);
      setHasAutoEnteredEditMode(true);
    }
  }, [startInEditMode, enrichedPatient, hasAutoEnteredEditMode, fertilityLoading]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setIsEditMode(false);
      setFormData({});
      setEnrichedPatient(null);
      setHasAutoEnteredEditMode(false);
      setActiveTab(0);
    }
  }, [open]);

  // Validate form data before allowing save
  const validateFormData = useCallback((formData) => {
    if (!formData.name?.trim()) return false;
    if (!formData.cpf?.trim()) return false;
    if (!formData.birth_date) return false;
    if (!formData.gender) return false;
    if (!formData.zip_code?.trim()) return false;
    if (!formData.city?.trim()) return false;
    if (!formData.address_state?.trim()) return false;
    if (!formData.address?.trim()) return false;
    if (!formData.address_number?.trim()) return false;
    // Validate contraceptive methods for female patients with pregnancy potential
    if (formData.gender === "F" && formData.has_pregnancy_potential) {
      if (!formData.highlyEffectiveMethods || formData.highlyEffectiveMethods.length === 0) {
        return false;
      }
      if (!formData.barrierMethods || formData.barrierMethods.length === 0) {
        return false;
      }
    }
    if (formData.gender === "F" && !formData.has_pregnancy_potential && !formData.selectedInfertilityReason) {
      return false;
    }
    return true;
  }, []);

  const handleEnterEditMode = useCallback(() => {
    if (!enrichedPatient) return;
    const prepared = prepareFormData(enrichedPatient);
    setFormData(prepared);
    setIsEditMode(true);
  }, [enrichedPatient]);

  const handleCancelEditMode = useCallback(() => {
    setIsEditMode(false);
    setFormData({});
  }, []);

  const handleFieldChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!enrichedPatient || !onSave) return;

    const result = await Swal.fire({
      title: intl.get("ConfirmSave").d("Confirm Save"),
      text: intl.get("ConfirmSavePatientMessage").d("Are you sure you want to save the patient changes?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: intl.get("Confirm").d("Confirm"),
      cancelButtonText: intl.get("Cancel").d("Cancel"),
    });

    if (!result.isConfirmed) return;

    setSaving(true);
    const payload = buildUpdatePayload(formData, enrichedPatient);
    const updatedData = await onSave(enrichedPatient.pk, payload);

    // Save fertility data if patient is female
    if (formData.gender === "F") {
      if (formData.has_pregnancy_potential) {
        try {
          // Step 1: Use existing methods from enrichedPatient (already loaded with fresh data)
          // This avoids calling the problematic get_all_methods endpoint that returns cached data
          const existingHighlyEffective = enrichedPatient.highly_effective_methods || [];
          const existingBarrier = enrichedPatient.barrier_methods || [];
          const existingPks = [
            ...existingHighlyEffective.map(m => m.pk),
            ...existingBarrier.map(m => m.pk)
          ];

          // Step 2: Calculate diff
          const newPks = [...(formData.highlyEffectiveMethods || []), ...(formData.barrierMethods || [])];
          const toAdd = newPks.filter(pk => !existingPks.includes(pk));
          const toRemove = existingPks.filter(pk => !newPks.includes(pk));

          // Step 3: Add new methods
          for (const methodPk of toAdd) {
            await fetchData("/patient_contraceptive_method", "POST", true, {
              patient_pk: enrichedPatient.pk,
              contraceptive_method_pk: methodPk
            });
          }

          // Step 4: Remove unchecked methods
          if (toRemove.length > 0) {
            const allAssociations = await fetchData(
              `/patient_contraceptive_method/get_associations/${enrichedPatient.pk}`,
              'GET',
              true
            );

            for (const methodPk of toRemove) {
              const association = allAssociations.data?.data?.find(
                assoc => assoc.contraceptive_method_pk === methodPk
              );
              if (association) {
                await fetchData(
                  `/patient_contraceptive_method/${association.pk}`,
                  'DELETE',
                  true
                );
              }
            }
          }
        } catch (error) {
          console.error("Error updating contraceptive methods:", error);
          // Don't block patient update on contraceptive method error
        }
      }

      if (!formData.has_pregnancy_potential && formData.selectedInfertilityReason) {
        try {
          await fetchData(
            `/patient_infertility_reason/update_infertility_reason/${enrichedPatient.pk}?infertility_reason_pk=${formData.selectedInfertilityReason}`,
            'PUT',
            true
          );
        } catch (error) {
          try {
            await fetchData("/patient_infertility_reason", "POST", true, {
              patient_pk: enrichedPatient.pk,
              infertility_reason_pk: formData.selectedInfertilityReason
            });
          } catch (postError) {
            console.error("Error saving infertility reason:", postError);
          }
        }
      }
    }

    setSaving(false);

    if (updatedData) {
      // Reload patient data AFTER fertility data was updated
      const freshDataResponse = await fetchData(`/patient/${enrichedPatient.pk}/details`, 'GET', true);
      const freshData = freshDataResponse.status === HTTP_STATUS.OK_200 ? freshDataResponse.data : updatedData;

      // Force reload to ensure all data is fresh
      setEnrichedPatient(null);
      const enrichedResult = await loadFertilityData(freshData);

      setEnrichedPatient(enrichedResult);
      setIsEditMode(false);
      setFormData({});
      onPatientUpdated?.();
      // Refresh history table
      historyTable.current?.updateTable();
    }
  }, [enrichedPatient, formData, onSave, onPatientUpdated, loadFertilityData]);

  const handleClose = useCallback(() => {
    setIsEditMode(false);
    setFormData({});
    onClose();
  }, [onClose]);

  const isLoading = loading || fertilityLoading;
  const showEditButton = canEdit !== false && !isEditMode && !isLoading && enrichedPatient;
  const isFormValid = validateFormData(formData);

  const modalTitle = isEditMode
    ? intl.get("EditPatientDetails").d("Edit Patient Details")
    : intl.get("PatientDetails").d("Patient Details");

  if (!open) return null;

  const tabs = [
    {
      name: "details",
      title: intl.get("Details").d("Details"),
      component: (
        <Box sx={{ p: isMobile ? 1 : 2 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            enrichedPatient && (
              isEditMode ? (
                <EditModeContent
                  data={enrichedPatient}
                  formData={formData}
                  onFieldChange={handleFieldChange}
                />
              ) : (
                <ViewModeContent data={enrichedPatient} />
              )
            )
          )}
        </Box>
      )
    },
    {
      title: intl.get("PrescriptionHistory").d("Prescription History"),
      icon: <DescriptionIcon fontSize="small" />,
      component: enrichedPatient ? (
        <PatientPrescriptionHistoryTab patientPk={enrichedPatient.pk} />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )
    },
    {
      title: intl.get("PatientHistory").d("Patient History"),
      icon: <HistoryIcon fontSize="small" />,
      component: (
        <Box sx={{ p: isMobile ? 1 : 2 }}>
          {enrichedPatient ? (
            <DmsTable
              header={getHistoryHeader()}
              managed={true}
              needs_authentication={true}
              ref={historyTable}
              url={`/patient/${enrichedPatient.pk}/status_history`}
              export_table={false}
            />
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      )
    }
  ];

  return (
    <DmsModal
      isVisible={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DmsModal.Header
        title={modalTitle}
        onClose={handleClose}
      />
      <DmsModal.Body>
        <DmsTabber
          selected={activeTab}
          onChange={(key) => setActiveTab(key)}
          tabs={tabs}
        />
      </DmsModal.Body>
      <DmsModal.Footer>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "flex-end",
            gap: 1,
            width: "100%",
            p: isMobile ? 1 : 2,
          }}
        >
          {isEditMode ? (
            <>
              <DmsButton
                icon={<CancelIcon />}
                text={intl.get("Cancel").d("Cancel")}
                onClick={handleCancelEditMode}
                disabled={saving}
                fullWidth={isMobile}
              />
              <DmsButton
                icon={<SaveIcon />}
                text={saving ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
                onClick={handleSave}
                disabled={saving || !isFormValid}
                fullWidth={isMobile}
              />
            </>
          ) : (
            <>
              <DmsButton
                text={intl.get("Close").d("Close")}
                onClick={handleClose}
                fullWidth={isMobile}
              />
              {showEditButton && (activeTab === 0 || activeTab === "details") && (
                <DmsButton
                  icon={<EditIcon />}
                  text={intl.get("Edit").d("Edit")}
                  onClick={handleEnterEditMode}
                  fullWidth={isMobile}
                />
              )}
            </>
          )}
        </Box>
      </DmsModal.Footer>
    </DmsModal>
  );
};

PatientDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patient: PropTypes.object,
  loading: PropTypes.bool,
  onSave: PropTypes.func,
  onPatientUpdated: PropTypes.func,
  canEdit: PropTypes.bool,
  startInEditMode: PropTypes.bool,
};

